import { SecurityHelper } from './helpers.js';
import {
  PRICE_CONTEXT_RANGE,
  DESCRIPTION_CONTEXT_RANGE,
  MAX_OG_DESCRIPTION_LENGTH,
  MIN_DESCRIPTION_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  SWIPE_THRESHOLD
} from './constants.js';

export function createProductParser(apiBaseUrl) {
  return {
    extractUrls(text) {
      const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi;
      return text.match(urlRegex) || [];
    },

    isProductUrl(url) {
      const productPatterns = [
        /\/products?\//i,
        /\/product\//i,
        /\/p\//i,
        /\/item\//i,
        /\/shop\//i,
        /myshopify\.com/i,
        /\/collections\/.+\/products\//i
      ];
      return productPatterns.some(pattern => pattern.test(url));
    },

    extractProductNameFromUrl(url) {
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/').filter(p => p);
        const productSlug = pathParts[pathParts.length - 1];
        return productSlug
          .replace(/[-_]/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase())
          .replace(/\.\w+$/, '');
      } catch (e) {
        return 'Produit';
      }
    },

    extractPriceNearUrl(text, urlPosition) {
      const contextBefore = text.substring(Math.max(0, urlPosition - PRICE_CONTEXT_RANGE), urlPosition);
      const contextAfter = text.substring(urlPosition, Math.min(text.length, urlPosition + PRICE_CONTEXT_RANGE));
      const context = contextBefore + contextAfter;

      const pricePatterns = [
        /(\d+[,.]?\d*)\s*€/,
        /€\s*(\d+[,.]?\d*)/,
        /(\d+[,.]?\d*)\s*EUR/i,
        /(\d+[,.]?\d*)\s*dollars?/i,
        /\$\s*(\d+[,.]?\d*)/,
        /(\d+[,.]?\d*)\s*\$/,
        /prix\s*:?\s*(\d+[,.]?\d*)/i,
        /(\d+[,.]?\d*)\s*CHF/i
      ];

      for (const pattern of pricePatterns) {
        const match = context.match(pattern);
        if (match) {
          const price = match[1] || match[0];
          if (pattern.toString().includes('€')) return price + ' €';
          if (pattern.toString().includes('$')) return price + ' $';
          if (pattern.toString().includes('CHF')) return price + ' CHF';
          return price + ' €';
        }
      }

      return null;
    },

    extractDescriptionNearUrl(text, urlPosition) {
      const contextBefore = text.substring(Math.max(0, urlPosition - DESCRIPTION_CONTEXT_RANGE), urlPosition);
      const sentences = contextBefore.split(/[.!?]\s+/);
      const lastSentence = sentences[sentences.length - 1];

      if (lastSentence && lastSentence.length > MIN_DESCRIPTION_LENGTH && lastSentence.length < MAX_DESCRIPTION_LENGTH) {
        return lastSentence.trim();
      }

      return null;
    },

    async fetchOpenGraphData(url) {
      try {
        const proxyUrl = `${apiBaseUrl}/fetch-og-data?url=${encodeURIComponent(url)}`;
        const response = await fetch(proxyUrl, {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });

        if (response.ok) {
          const data = await response.json();
          return {
            title: data.ogTitle || data.title,
            description: data.ogDescription || data.description,
            image: data.ogImage || data.image,
            price: data.price
          };
        }
      } catch (e) {
        console.log('Impossible de récupérer les métadonnées OG:', e);
      }
      return null;
    },

    async parseProductsFromText(text) {
      const urls = this.extractUrls(text);
      const productUrls = urls.filter(url => this.isProductUrl(url));

      if (productUrls.length === 0) return null;

      const products = [];

      for (const url of productUrls) {
        const urlPosition = text.indexOf(url);
        const ogData = await this.fetchOpenGraphData(url);

        const product = {
          name: this.extractProductNameFromUrl(url),
          url: url,
          image: null,
          price: this.extractPriceNearUrl(text, urlPosition),
          description: this.extractDescriptionNearUrl(text, urlPosition)
        };

        if (ogData) {
          if (ogData.title) product.name = ogData.title;
          if (ogData.image) product.image = ogData.image;
          if (ogData.price) product.price = ogData.price;
          if (ogData.description && !product.description) {
            product.description = ogData.description.substring(0, MAX_OG_DESCRIPTION_LENGTH);
          }
        }

        products.push(product);
      }

      return products.length > 0 ? products : null;
    },

    removeUrlsFromText(text) {
      const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi;
      return text.replace(urlRegex, '').replace(/\s+/g, ' ').trim();
    }
  };
}

export function createProductCarousel(products, messageText, t) {
  if (!products || !Array.isArray(products) || products.length === 0) {
    return null;
  }

  const carouselId = 'carousel-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

  const totalSlides = products.length;
  const sliderLabel = t ? t.productSlider : 'Carrousel de produits';
  const prevLabel = t ? t.prevSlide : 'Précédent';
  const nextLabel = t ? t.nextSlide : 'Suivant';

  const slides = products.map((product, index) => {
    const slideId = `${carouselId}-slide-${index}`;
    const rawImageSrc = product.image || product.imageUrl || product.img || '';
    const imageSrc = SecurityHelper.escapeAttribute(SecurityHelper.sanitizeURL(rawImageSrc));
    const fallbackName = t ? t.productFallbackName : 'Produit';
    const safeName = SecurityHelper.escapeHTML(product.name || fallbackName);
    const imageHTML = imageSrc
      ? `<img src="${imageSrc}" alt="${safeName}" class="product-image">`
      : '<div class="product-image-placeholder" aria-hidden="true">🛍️</div>';

    const badge = product.badge
      ? `<span class="product-badge ${product.inStock === false ? 'out-of-stock' : ''}">${SecurityHelper.escapeHTML(product.badge)}</span>`
      : '';

    const description = product.description
      ? `<div class="product-description">${SecurityHelper.escapeHTML(product.description)}</div>`
      : '';

    const rawUrl = product.url || product.link || '#';
    const url = rawUrl === '#' ? '#' : SecurityHelper.escapeAttribute(SecurityHelper.sanitizeURL(rawUrl));
    const defaultLinkText = t ? t.viewProduct : 'Voir le produit';
    const linkText = SecurityHelper.escapeHTML(product.linkText || product.buttonText || defaultLinkText);
    const slideLabel = t && typeof t.slideOf === 'function'
      ? t.slideOf(index + 1, totalSlides)
      : `${index + 1} / ${totalSlides}`;

    return `
      <div class="product-carousel-slide" id="${slideId}" role="group" aria-roledescription="slide" aria-label="${SecurityHelper.escapeAttribute(slideLabel)}">
        <div class="product-image-container">
          ${imageHTML}
        </div>
        <div class="product-info">
          ${badge}
          <div class="product-name">${safeName}</div>
          ${product.price ? `<div class="product-price">${SecurityHelper.escapeHTML(product.price)}</div>` : ''}
          ${description}
          ${url !== '#' ? `<a href="${url}" target="_blank" rel="noopener noreferrer" class="product-link">${linkText}</a>` : ''}
        </div>
      </div>
    `;
  }).join('');

  const indicators = products.map((_, index) => {
    const slideId = `${carouselId}-slide-${index}`;
    const indicatorLabel = t && typeof t.slideOf === 'function'
      ? t.slideOf(index + 1, totalSlides)
      : `${index + 1} / ${totalSlides}`;
    return `<button type="button" class="carousel-indicator ${index === 0 ? 'active' : ''}" data-index="${index}" role="tab" aria-selected="${index === 0 ? 'true' : 'false'}" aria-controls="${slideId}" aria-label="${SecurityHelper.escapeAttribute(indicatorLabel)}" tabindex="${index === 0 ? '0' : '-1'}"></button>`;
  }).join('');

  const safeMessageText = messageText ? SecurityHelper.sanitizeHTML(messageText) : '';

  const carouselHTML = `
    <div class="product-carousel-container">
      ${safeMessageText ? `<div class="bubble" style="margin-bottom: 8px !important;">${safeMessageText}</div>` : ''}
      <div class="product-carousel" data-carousel-id="${carouselId}" role="region" aria-roledescription="carousel" aria-label="${SecurityHelper.escapeAttribute(sliderLabel)}" tabindex="0">
        <div class="product-carousel-header">
          <div class="product-carousel-title">
            <span aria-hidden="true">🛍️ </span>${t ? t.recommendedProducts : 'Produits recommandés'}
          </div>
          <div class="product-carousel-count">${t ? t.productCount(products.length) : `${products.length} ${products.length > 1 ? 'produits' : 'produit'}`}</div>
        </div>
        <div class="product-carousel-track-container">
          ${products.length > 1 ? `
            <button type="button" class="carousel-nav carousel-nav-prev" data-direction="prev" aria-label="${SecurityHelper.escapeAttribute(prevLabel)}"><span aria-hidden="true">‹</span></button>
            <button type="button" class="carousel-nav carousel-nav-next" data-direction="next" aria-label="${SecurityHelper.escapeAttribute(nextLabel)}"><span aria-hidden="true">›</span></button>
          ` : ''}
          <div class="product-carousel-track" data-current-slide="0" aria-live="polite">
            ${slides}
          </div>
        </div>
        ${products.length > 1 ? `
          <div class="carousel-indicators" role="tablist" aria-label="${SecurityHelper.escapeAttribute(sliderLabel)}">
            ${indicators}
          </div>
        ` : ''}
      </div>
    </div>
  `;

  return { html: carouselHTML, carouselId: carouselId, productCount: products.length };
}

export function initializeCarousel(carouselId, el) {
  const carousel = el.querySelector(`[data-carousel-id="${carouselId}"]`);
  if (!carousel) return;

  const track = carousel.querySelector('.product-carousel-track');
  const prevBtn = carousel.querySelector('.carousel-nav-prev');
  const nextBtn = carousel.querySelector('.carousel-nav-next');
  const indicators = carousel.querySelectorAll('.carousel-indicator');

  if (!track) return;

  let currentSlide = 0;
  const totalSlides = track.children.length;

  function updateCarousel(focusActiveTab = false) {
    const offset = -currentSlide * 100;
    track.style.transform = `translateX(${offset}%)`;
    track.setAttribute('data-current-slide', currentSlide);

    indicators.forEach((indicator, index) => {
      const active = index === currentSlide;
      indicator.classList.toggle('active', active);
      indicator.setAttribute('aria-selected', active ? 'true' : 'false');
      indicator.setAttribute('tabindex', active ? '0' : '-1');
      if (active && focusActiveTab) indicator.focus();
    });

    if (prevBtn) {
      const atStart = currentSlide === 0;
      prevBtn.classList.toggle('disabled', atStart);
      prevBtn.disabled = atStart;
    }
    if (nextBtn) {
      const atEnd = currentSlide === totalSlides - 1;
      nextBtn.classList.toggle('disabled', atEnd);
      nextBtn.disabled = atEnd;
    }
  }

  function go(delta, focusTab = false) {
    const next = Math.max(0, Math.min(totalSlides - 1, currentSlide + delta));
    if (next !== currentSlide) {
      currentSlide = next;
      updateCarousel(focusTab);
    }
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      go(-1);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      go(1);
    });
  }

  indicators.forEach((indicator, index) => {
    indicator.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      currentSlide = index;
      updateCarousel();
    });
  });

  // Keyboard navigation: ←/→ from the carousel region or any tab moves slides.
  // Home/End jump to the first/last slide. Tab focus is preserved on the
  // active indicator when arrow keys are used from the tablist.
  const onKey = (e) => {
    let handled = false;
    const fromTab = e.target && e.target.classList && e.target.classList.contains('carousel-indicator');
    if (e.key === 'ArrowLeft') { go(-1, fromTab); handled = true; }
    else if (e.key === 'ArrowRight') { go(1, fromTab); handled = true; }
    else if (e.key === 'Home') {
      if (currentSlide !== 0) { currentSlide = 0; updateCarousel(fromTab); }
      handled = true;
    } else if (e.key === 'End') {
      if (currentSlide !== totalSlides - 1) { currentSlide = totalSlides - 1; updateCarousel(fromTab); }
      handled = true;
    }
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  carousel.addEventListener('keydown', onKey);

  let touchStartX = 0;
  let touchEndX = 0;

  track.addEventListener('touchstart', (e) => {
    touchStartX = e.changedTouches[0].screenX;
  }, { passive: true });

  track.addEventListener('touchend', (e) => {
    touchEndX = e.changedTouches[0].screenX;
    const diff = touchStartX - touchEndX;
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff > 0) go(1);
      else go(-1);
    }
  }, { passive: true });

  updateCarousel();
}
